import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import '@bryntum/gantt/gantt.stockholm.css';
import { Cascader, Col, ConfigProvider, InputRef, Popover, Row, Segmented, Select, Space } from 'antd';
import { ExclamationCircleOutlined, QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons';
import './bryntum-gantt.scss';
import moment, { Moment } from 'moment-timezone';
import { RENEWAL_RECORD_TYPE, RENEWAL_ZONE_CONTENT } from 'shared/constants';
import MultiSelect from '../../shared/MultiSelect';
import { GanttChartMode } from 'sb/shared/constants';
import { getFilteredTasks } from './RenewalGanttChartFilters';
import { cloneDeep } from 'lodash';
import { FieldSorter } from 'shared/Helpers';
import { RenewalChildRecord, RenewalRecord } from 'models/Renewals';
import { BryntumGantt } from '@bryntum/gantt-react';
import { PortfolioSelector } from 'components/dashboard/PortfolioSelector';
import { ClientPortfolio } from 'api/useClient';
import {
  CalendarStatus,
  ClientContactOption,
  ExpiresInPreset,
  GroupView,
  RenewalZone,
  SmartCalendarPrefs,
  expiresInPresetToDate,
  writeSmartCalendarPrefs
} from './smartCalendarPrefs';

/**
 * Extracts vendor name from a deal name that follows "VendorName: DealDescription" pattern
 * @param dealName - The full deal name
 * @returns The extracted vendor name or the original name if no pattern match
 */
const extractVendorNameFromDeal = (dealName: string): string => {
  if (!dealName) return 'Unknown Vendor';
  const colonIndex = dealName.indexOf(':');
  if (colonIndex > 0) {
    return dealName.substring(0, colonIndex).trim();
  }
  return dealName;
};

type SupplierDynamicPoint = {
  value: number;
  dealDynamic: {
    key: string;
    name: string;
    scoreTooltips?: string[];
  };
};

const DYNAMIC_METRIC_NAMES: Record<string, string> = {
  vendorComplexity: 'Vendor Complexity',
  pricingFlexibility: 'Pricing Flexibility',
  dealSizeTrend: 'Deal Size Trend'
};

/**
 * Normalizes and clamps dynamic values for consistent rendering.
 * @param key - Dynamic metric key
 * @param value - Numeric value to normalize
 * @returns Normalized numeric value
 */
const normalizeDynamicValue = (key: string, value: number): number => {
  if (key === 'vendorComplexity' || key === 'pricingFlexibility') {
    return Math.max(1, Math.min(3, Math.round(value)));
  }
  return Number(value.toFixed(2));
};

/**
 * Extracts dynamic metric points from a deal record.
 * Supports both supplierDynamics and legacy sd payload shapes.
 * @param deal - Deal record
 * @returns Dynamic points keyed by dynamic metric
 */
const extractDynamicPointsFromDeal = (deal: RenewalChildRecord): SupplierDynamicPoint[] => {
  const dynamicMap = new Map<string, SupplierDynamicPoint>();
  const supplierDynamics = (deal as any)?.supplierDynamics;

  if (Array.isArray(supplierDynamics)) {
    supplierDynamics.forEach((entry: any) => {
      const meta = entry?.dealDynamic || entry?.deal_dynamic;
      const key = meta?.key;
      const value = Number(entry?.value);

      if (!key || Number.isNaN(value)) {
        return;
      }

      dynamicMap.set(key, {
        value,
        dealDynamic: {
          key,
          name: meta?.name || DYNAMIC_METRIC_NAMES[key] || key,
          scoreTooltips: meta?.scoreTooltips || meta?.score_tooltips
        }
      });
    });
  }

  const sd = (deal as any)?.sd;
  if (Array.isArray(sd)) {
    sd.forEach((entry: any) => {
      const key = entry?.key;
      const value = Number(entry?.mean);

      if (!key || Number.isNaN(value) || dynamicMap.has(key)) {
        return;
      }

      dynamicMap.set(key, {
        value,
        dealDynamic: {
          key,
          name: entry?.name || DYNAMIC_METRIC_NAMES[key] || key
        }
      });
    });
  }

  return Array.from(dynamicMap.values());
};

/**
 * Aggregates dynamic metrics for a vendor group from its child deals.
 * @param deals - Vendor deal children
 * @returns Supplier dynamics array expected by gantt dynamic columns
 */
const aggregateSupplierDynamics = (deals: RenewalChildRecord[]): SupplierDynamicPoint[] => {
  const sumsByKey = new Map<string, { sum: number; count: number; meta: SupplierDynamicPoint['dealDynamic'] }>();

  deals.forEach((deal) => {
    const points = extractDynamicPointsFromDeal(deal);
    points.forEach((point) => {
      const current = sumsByKey.get(point.dealDynamic.key);
      if (current) {
        current.sum += point.value;
        current.count += 1;
      } else {
        sumsByKey.set(point.dealDynamic.key, {
          sum: point.value,
          count: 1,
          meta: point.dealDynamic
        });
      }
    });
  });

  return Array.from(sumsByKey.entries()).map(([key, aggregate]) => ({
    value: normalizeDynamicValue(key, aggregate.sum / aggregate.count),
    dealDynamic: aggregate.meta
  }));
};

/**
 * Checks if a deal matches the zone filter criteria
 * @param deal - The deal record to check
 * @param zone - The zone filter
 * @returns Whether the deal matches the zone
 */
export const dealMatchesZone = (deal: RenewalChildRecord, zone: 'red' | 'orange' | 'default' | 'expired'): boolean => {
  if (zone === 'default') return true;

  const runway = deal.runway || 3;
  const orangeRunway = 1;

  if (zone === 'red') {
    return deal.remaining >= 0 && deal.remaining <= runway;
  } else if (zone === 'orange') {
    return deal.remaining >= 0 && deal.remaining <= runway + orangeRunway;
  } else if (zone === 'expired') {
    return deal.remaining <= 0;
  }
  return true;
};

/**
 * Derives the name a node is displayed under in the Gantt name column, so the
 * default ordering matches what the user reads. Mirrors getNameValue in
 * BryntumConfig, including its status-dependent placeholders:
 *   - SUPPLIER groups render their vendor name.
 *   - SUPPLIER_DEAL renders "Vendor: Deal"; a Calendar deal with no subject
 *     falls back to "Vendor: --", a non-Calendar one to "Vendor: ".
 *   - DEAL renders the subject alone; a Calendar deal with no subject falls
 *     back to "--", a non-Calendar one to its internal name.
 * Fails closed to '' so a missing/nullish node never throws and simply sorts
 * first. For SUPPLIER, orgName is a fail-closed fallback only (getNameValue
 * uses name, which the synthesized groups always set).
 * @param node - Any renewal tree node produced by the group-view transform
 * @returns Lowercased display name used purely as the ascending sort key
 */
export const getSortableName = (node?: RenewalChildRecord | null): string => {
  if (node == null) return '';
  const { SUPPLIER, SUPPLIER_DEAL, DEAL } = RENEWAL_RECORD_TYPE;
  const orgName = (node as any).orgName || '';
  const subject = (node as any).subject || '';
  const name = node.name || '';
  const isCalendar = (node as any).status === 'Calendar';

  let displayName: string;
  if (node.recordType === SUPPLIER) {
    displayName = name || orgName;
  } else if (node.recordType === SUPPLIER_DEAL) {
    displayName = `${orgName}: ${subject || (isCalendar ? '--' : '')}`;
  } else if (node.recordType === DEAL) {
    displayName = subject || (isCalendar ? '--' : name);
  } else {
    displayName = name || orgName;
  }

  return displayName.toLowerCase().trim();
};

/**
 * Ascending, case-insensitive comparator ordering renewal nodes by their
 * displayed name. Used to give every group view a stable default order.
 * @param a - Left node
 * @param b - Right node
 * @returns Negative/zero/positive per Array.prototype.sort contract
 */
const compareRenewalNodesByName = (a?: RenewalChildRecord | null, b?: RenewalChildRecord | null): number =>
  FieldSorter(getSortableName(a), getSortableName(b));

/**
 * Collects all individual deals from the data structure (flattens SUPPLIER groups)
 * @param children - Array of child records
 * @returns Array of all individual deals with vendor names set and parentId removed
 */
export const collectAllDeals = (children: RenewalChildRecord[]): RenewalChildRecord[] => {
  const { SUPPLIER, SUPPLIER_DEAL, DEAL } = RENEWAL_RECORD_TYPE;
  const allDeals: RenewalChildRecord[] = [];

  children.forEach((record) => {
    if (record.recordType === SUPPLIER) {
      // Extract deals from SUPPLIER groups
      const vendorName = record.name || (record as any).orgName || 'Unknown Vendor';
      if (record.children && record.children.length > 0) {
        record.children.forEach((child) => {
          const clonedChild = cloneDeep(child);
          (clonedChild as any).orgName = vendorName;
          // IMPORTANT: Remove parentId so deals are at root level when flattened
          delete (clonedChild as any).parentId;
          allDeals.push(clonedChild);
        });
      }
    } else if (record.recordType === SUPPLIER_DEAL || record.recordType === DEAL) {
      // Standalone deals
      const clonedRecord = cloneDeep(record);
      if (!(clonedRecord as any).orgName) {
        (clonedRecord as any).orgName = extractVendorNameFromDeal(clonedRecord.name);
      }
      // IMPORTANT: Remove parentId so deals are at root level when flattened
      delete (clonedRecord as any).parentId;
      allDeals.push(clonedRecord);
    }
  });

  return allDeals;
};

/**
 * Groups deals by vendor name into SUPPLIER nodes
 * @param deals - Array of individual deals
 * @returns Array of SUPPLIER nodes with deals as children
 */
const groupDealsByVendor = (deals: RenewalChildRecord[]): RenewalChildRecord[] => {
  const { SUPPLIER, DEAL } = RENEWAL_RECORD_TYPE;
  const vendorMap = new Map<string, RenewalChildRecord>();
  let syntheticIdCounter = 900000000;

  deals.forEach((deal) => {
    const vendorName = (deal as any).orgName || extractVendorNameFromDeal(deal.name);

    if (!vendorMap.has(vendorName)) {
      syntheticIdCounter++;
      const supplierId = syntheticIdCounter;

      // Create a properly formatted child deal with parentId
      const childDeal = {
        ...deal,
        parentId: supplierId,
        // Ensure deals inside SUPPLIER groups have DEAL recordType for proper rendering
        recordType: DEAL
      } as RenewalChildRecord;

      vendorMap.set(vendorName, {
        id: supplierId,
        name: vendorName,
        orgName: vendorName,
        recordType: SUPPLIER,
        children: [childDeal],
        supplierDynamics: aggregateSupplierDynamics([childDeal]),
        expanded: false,
        leaf: false,
        readOnly: true,
        contractsCount: 1
      } as any as RenewalChildRecord);
    } else {
      const group = vendorMap.get(vendorName);
      if (group && group.children) {
        // Create a properly formatted child deal with parentId
        const childDeal = {
          ...deal,
          parentId: group.id,
          recordType: DEAL
        } as RenewalChildRecord;
        group.children.push(childDeal);
        (group as any).supplierDynamics = aggregateSupplierDynamics(group.children);
        // Update contractsCount
        (group as any).contractsCount = group.children.length;
      }
    }
  });

  const groups = Array.from(vendorMap.values());
  // Order the deals within each vendor group ascending by name so the default
  // ordering is consistent when a vendor is expanded (VE-1998).
  groups.forEach((group) => {
    if (Array.isArray(group.children)) {
      group.children.sort(compareRenewalNodesByName);
    }
  });
  return groups;
};

/**
 * Main function to prepare data for rendering based on all filters and group view
 * This function is called whenever any filter changes to ensure consistent data transformation
 * @param originalChildren - Original children from _tasks (hybrid structure)
 * @param groupView - Selected group view (hybrid/flat/vendor)
 * @param zone - Selected zone filter
 * @param clientContactFilterValue - Client contact filter values
 * @param selectedCategories - Selected category filter values
 * @param _tasks - Original tasks for client/category filtering
 * @returns Transformed children array ready for rendering
 */
export const prepareDataForRender = (
  originalChildren: RenewalChildRecord[],
  groupView: GroupView,
  zone: 'red' | 'orange' | 'default' | 'expired',
  clientContactFilterValue: Array<string | number>,
  selectedCategories: string[],
  _tasks: RenewalRecord[]
): RenewalChildRecord[] => {
  // Step 1: Apply client contact and category filters first (on original hybrid structure)
  let workingChildren: RenewalChildRecord[];
  if (clientContactFilterValue.length > 0 || selectedCategories.length > 0) {
    workingChildren = getFilteredTasks({
      clientContactFilterValue,
      _tasks,
      selectedCategories
    }) as RenewalChildRecord[];
  } else {
    workingChildren = cloneDeep(originalChildren);
  }

  // Step 2: Collect all individual deals (flatten the structure)
  const allDeals = collectAllDeals(workingChildren);

  // Step 3: Apply zone filter to individual deals
  const zoneFilteredDeals = zone === 'default' ? allDeals : allDeals.filter((deal) => dealMatchesZone(deal, zone));

  // Step 4: Transform based on group view
  if (groupView === 'flat') {
    // Flat view: return individual deals as standalone items (SUPPLIER_DEAL type for proper rendering)
    const { SUPPLIER_DEAL } = RENEWAL_RECORD_TYPE;
    return zoneFilteredDeals
      .map((deal) => {
        const flatDeal = {
          ...deal,
          recordType: SUPPLIER_DEAL, // Ensure flat view shows deals with vendor prefix
          leaf: true // Mark as leaf node (no children)
        };
        // IMPORTANT: Delete parentId entirely so Bryntum doesn't apply tree indentation
        delete (flatDeal as any).parentId;
        delete (flatDeal as any).children; // Remove any children property
        return flatDeal;
      })
      .sort(compareRenewalNodesByName); // Ascending by "Vendor: Deal" name (VE-1998)
  }

  if (groupView === 'vendor') {
    // Vendor view: group all deals by vendor (groups sorted ascending by vendor name, VE-1998)
    return groupDealsByVendor(zoneFilteredDeals).sort(compareRenewalNodesByName);
  }

  // Hybrid view: recreate the original structure with filtered data
  // Group deals by vendor, but keep multi-deal vendors as groups and single-deal vendors as standalone
  const { SUPPLIER_DEAL } = RENEWAL_RECORD_TYPE;
  const vendorGroups = groupDealsByVendor(zoneFilteredDeals);
  const hybridResult: RenewalChildRecord[] = [];

  vendorGroups.forEach((group) => {
    if (group.children && group.children.length === 1) {
      // Single deal for this vendor - show as standalone deal (not wrapped in group)
      // Convert to SUPPLIER_DEAL for proper standalone rendering with vendor prefix
      const standaloneDeal = {
        ...group.children[0],
        recordType: SUPPLIER_DEAL,
        leaf: true // Mark as leaf node
      };
      // IMPORTANT: Delete parentId entirely so Bryntum doesn't apply tree indentation
      delete (standaloneDeal as any).parentId;
      delete (standaloneDeal as any).children;
      hybridResult.push(standaloneDeal as RenewalChildRecord);
    } else if (group.children && group.children.length > 1) {
      // Multiple deals for this vendor - keep as grouped SUPPLIER
      hybridResult.push(group);
    }
  });

  // Ascending by name across both standalone deals and vendor groups (VE-1998)
  return hybridResult.sort(compareRenewalNodesByName);
};

interface Props {
  mode?: GanttChartMode;
  _tasks: RenewalRecord[];
  startDate: Date | undefined;
  endDate: Date | undefined;
  portfolioCompanies: Array<{ id: number; name: string }>;
  isGrandFather: boolean;
  categories: any[];
  status: 'active' | 'expired';
  ganttRef: React.RefObject<BryntumGantt>;
  setTasks: (tasks: RenewalRecord[]) => void;
  vendorFilterRef: React.RefObject<InputRef>;
  setStatus: (status: 'active' | 'expired') => void;
  setExpiresIn: (date: Date | undefined) => void;
  clientPortfolio: ClientPortfolio[];
  handlePortfolioSelection: (client_id: number | null) => void;
  groupView: 'hybrid' | 'flat' | 'vendor';
  setGroupView: (view: 'hybrid' | 'flat' | 'vendor') => void;
  /**
   * Where this user's selections for this calendar are stored (VE-2259), or null while the
   * signed-in user is unknown — in which case nothing is saved, because there is no bucket that
   * belongs to this session and a shared one would leak between users (VE-2605).
   */
  prefsStorageKey: string | null;
  /** The selections restored at mount, used to seed every filter below. */
  initialPrefs: SmartCalendarPrefs;
}

export const RenewalGanttChartCardTitle: React.FC<Props> = ({
  mode = GanttChartMode.OWN_CALENDER,
  _tasks,
  setTasks,
  startDate,
  endDate,
  status,
  ganttRef,
  setStatus,
  categories,
  setExpiresIn,
  clientPortfolio,
  handlePortfolioSelection,
  groupView,
  setGroupView,
  prefsStorageKey,
  initialPrefs
}) => {
  const portfolioFilterRef = createRef<InputRef>();
  const [segmentedClass, setSegmentedClass] = useState<RenewalZone>(initialPrefs.renewalZone);
  const [cascaderKey, _setCascaderKey] = useState<number>(0);
  const [, setDateRange] = useState<[Moment, Moment] | undefined>(undefined);
  const [dateSelector] = useState<'preset' | 'custom'>('preset');
  const [clientContactFilterValue, setClientContactFilterValue] = useState<Array<string | number>>(
    initialPrefs.clientContacts
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialPrefs.categories);
  // Expires In is held here as the PRESET the toolbar shows, while the parent holds the date it
  // resolves to. Storing the preset is what stops a saved "1 Year" going stale (see
  // expiresInPresetToDate).
  const [expiresInPreset, setExpiresInPreset] = useState<ExpiresInPreset>(initialPrefs.expiresIn);
  const { SUPPLIER, SUPPLIER_DEAL, DEAL } = RENEWAL_RECORD_TYPE;

  // Every selection is written as one complete object, so the six filters always agree with each
  // other on disk and a cleared filter is recorded as cleared rather than omitted. The snapshot
  // lives in a ref, refreshed after each commit, because `handleZoneClick` is memoised: a closure
  // captured on an earlier render would otherwise persist that render's stale sibling values.
  const latestPrefs = useRef<SmartCalendarPrefs>(initialPrefs);
  useEffect(() => {
    latestPrefs.current = {
      groupView,
      categories: selectedCategories,
      clientContacts: clientContactFilterValue,
      expiresIn: expiresInPreset,
      status,
      renewalZone: segmentedClass
    };
  }, [groupView, selectedCategories, clientContactFilterValue, expiresInPreset, status, segmentedClass]);

  /**
   * Saves the toolbar's selections, with `changes` applied on top of the last committed snapshot.
   *
   * The change being made is passed explicitly rather than read back from state, because state
   * updates have not flushed yet at the point a handler calls this.
   * @param {Partial<SmartCalendarPrefs>} changes - The selection(s) the user just made.
   * @returns {void}
   */
  const persistPrefs = useCallback(
    (changes: Partial<SmartCalendarPrefs>): void => {
      writeSmartCalendarPrefs(prefsStorageKey, { ...latestPrefs.current, ...changes });
    },
    [prefsStorageKey]
  );

  const handleGroupViewChange = (value: any) => {
    const groupViewValue = value as GroupView;
    setGroupView(groupViewValue);
    persistPrefs({ groupView: groupViewValue });
    // Reset expand/collapse state when switching views
    if (ganttRef.current) {
      ganttRef.current.instance.collapseAll();
    }
  };

  const handleDataChange = (value: ExpiresInPreset): void => {
    setExpiresInPreset(value);
    setExpiresIn(expiresInPresetToDate(value));
    persistPrefs({ expiresIn: value });
  };

  /**
   * Sets the status based on the provided value.
   * @param {string} status - The status to be set. It should be either 'active' or 'expired'.
   * @returns {void}
   */
  const handleActiveExpiredClick = (status: CalendarStatus): void => {
    setStatus(status);
    persistPrefs({ status });
  };

  // VE-2110: this used to add a fresh `refresh` -> `zoomToFit` listener here on every call, none
  // of them ever detached, so each filter change left one more subscriber re-fitting the calendar
  // and discarding the user's zoom. The single arbitrated auto-fit now lives in RenewalGanttChart,
  // which owns the `stateId` and can therefore tell a first visit from a restored zoom.
  const resetTasks = (tasksList: RenewalRecord[]) => {
    if (mode === GanttChartMode.OWN_CALENDER) {
      setTasks(tasksList);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!_tasks || !_tasks[0]) {
        resetTasks([]);
        return;
      }

      // Use the unified prepareDataForRender function
      // This function handles all filtering and transformation in one place
      const preparedChildren = prepareDataForRender(
        _tasks[0].children as RenewalChildRecord[],
        groupView,
        segmentedClass,
        clientContactFilterValue,
        selectedCategories,
        _tasks
      );

      // Pass prepared children directly as top-level tasks (no root wrapping)
      resetTasks(preparedChildren as unknown as RenewalRecord[]);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientContactFilterValue, mode, selectedCategories, groupView, _tasks, segmentedClass]);

  useEffect(() => {
    if (mode !== GanttChartMode.OWN_CALENDER) {
      portfolioFilterRef.current?.focus();
    }
  }, [mode, portfolioFilterRef]);

  const getUserOptions = (): Array<{ value: number | string; label: string }> => {
    const usersList: Array<{ value: number | string; label: string }> = [];
    if (_tasks[0]?.children) {
      _tasks[0]?.children?.forEach((taskChild: RenewalChildRecord) => {
        if (taskChild?.recordType === SUPPLIER_DEAL) {
          const idx = usersList?.findIndex((user) => user.value === taskChild?.clientContact?.Id);
          taskChild?.clientContact &&
            idx === -1 &&
            usersList?.push({
              value: taskChild.clientContact?.Id,
              label: taskChild.clientContact?.Name
            });
        } else if (taskChild?.recordType === SUPPLIER && taskChild?.children?.length > 0) {
          taskChild?.children?.forEach((childChild: RenewalChildRecord) => {
            const idx = usersList?.findIndex((user) => user.value === childChild?.clientContact?.Id);
            childChild.recordType === DEAL &&
              childChild?.clientContact &&
              idx === -1 &&
              usersList.push({
                value: childChild.clientContact?.Id,
                label: childChild.clientContact?.Name
              });
          });
        }
      });
    }
    return usersList;
  };

  // A restored Client Contact id is never reconciled against this list. It looks like a complete
  // roster but is not: the renewals request is windowed server-side by Status and Expires In
  // (smart-calendar.service.ts, calendar-deals.service.ts), so a contact whose only deal falls
  // outside the current window is simply absent. Dropping "absent" ids would therefore delete a
  // still-valid selection — and no filter combination yields a full roster, since Active excludes
  // already-expired contracts and Expired excludes active ones.
  //
  // So a saved id that matches nothing filters the calendar to empty and shows a raw id in the
  // chip. That is recoverable — the saved value survives, and widening Expires In brings the deals
  // back — whereas discarding it would not be.
  const clientContactOptions: ClientContactOption[] = getUserOptions();

  /**
   * Handles the change of owner values.
   * If values are provided and the first value is not 'All', filters the tasks based on the owner values.
   * If values are not provided or the first value is 'All', sets the tasks back to the original tasks.
   * @param {Array<string | number>} values - The owner values to be applied for filtering tasks
   * @returns {void}
   */
  const handleOwnerChange = (values: Array<string | number>): void => {
    const selectedContacts = values?.length > 0 && values[0] !== 'All' ? values : [];
    setClientContactFilterValue(selectedContacts);
    persistPrefs({ clientContacts: selectedContacts });
  };

  const handleCategoryChange = (value: any): void => {
    const newValue = Array.isArray(value) ? value : [];
    setSelectedCategories(newValue);
    persistPrefs({ categories: newValue });
  };

  const handleZoneClick = useCallback(
    (zone: RenewalZone) => {
      setSegmentedClass(zone);
      persistPrefs({ renewalZone: zone });

      if (zone === 'default') {
        setDateRange([moment(startDate), moment(endDate)]);
      }
    },
    // eslint-disable-next-line
    [endDate, startDate, persistPrefs]
  );

  return (
    <ConfigProvider componentSize={'large'}>
      <Row justify={'space-between'} className={'gap-5'}>
        <Col className={'grow'}>
          <Space>
            {mode == GanttChartMode.OWN_CALENDER && (
              <>
                {dateSelector === 'preset' && (
                  <>
                    <div>
                      <div className={'filter-label'}>Filter by Category</div>
                      <Cascader
                        key={cascaderKey}
                        className={'w-36 custom-cascader'}
                        size={'large'}
                        placeholder="Filter by Categories"
                        options={categories
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((rootCategory: any) => ({
                            value: rootCategory.name,
                            label: rootCategory.name,
                            title: rootCategory.name,
                            children: rootCategory.category_prime
                              ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
                              .map((primeCategory: any) => ({
                                value: primeCategory.name,
                                label: primeCategory.name,
                                title: primeCategory.name,
                                children: primeCategory.category_second
                                  ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
                                  .map((secondCategory: any) => ({
                                    value: secondCategory.name,
                                    label: secondCategory.name,
                                    title: secondCategory.name,
                                    isLeaf: true
                                  }))
                              }))
                          }))}
                        multiple
                        maxTagCount={1}
                        showSearch={{
                          filter: (inputValue, path) => {
                            return path.some((option) => {
                              const label = option.label;
                              if (typeof label === 'string') {
                                return label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
                              }
                              return false;
                            });
                          }
                        }}
                        value={selectedCategories}
                        onChange={handleCategoryChange}
                        onClear={() => {
                          setSelectedCategories([]);
                          persistPrefs({ categories: [] });
                        }}
                        allowClear
                        notFoundContent="No categories found"
                        style={{ width: 220 }}
                      />
                    </div>
                  </>
                )}
                <div>
                  <div className={'filter-label'}>Client Contact</div>
                  <MultiSelect
                    options={clientContactOptions}
                    value={clientContactFilterValue.length > 0 ? clientContactFilterValue : ['All']}
                    onChange={handleOwnerChange}
                    displayLabelAll={true}
                    maxTagCount={4}
                  />
                </div>
              </>
            )}
            {mode === GanttChartMode.OWN_CALENDER && (
              <div>
                <div className={'filter-label'}>Group View</div>
                <Select
                  size={'large'}
                  className="w-48"
                  value={groupView}
                  onChange={handleGroupViewChange}
                  options={[
                    { label: 'Hybrid', value: 'hybrid' },
                    { label: 'Flat', value: 'flat' },
                    { label: 'Vendor', value: 'vendor' }
                  ]}
                />
              </div>
            )}
            {mode === GanttChartMode.PORTFOLIO && clientPortfolio.length > 0 && (
              <PortfolioSelector
                clientPortfolio={clientPortfolio}
                onSelection={handlePortfolioSelection}
                bodyStyle={{}}
                className="mb-0"
              />
            )}
          </Space>
        </Col>
        <Col className={'flex justify-end'}>
          <Space size={'large'}>
            <div style={{ width: 200 }}>
              <div className={'filter-label'}>Expires In</div>
              <Segmented
                size={'large'}
                block
                className={status === 'active' ? 'npi-blue' : 'default'}
                value={expiresInPreset}
                onChange={(value: any) => handleDataChange(value)}
                options={[
                  { label: '1 Year', value: 'one_year' },
                  { label: 'Full', value: 'full' }
                ]}
              />
            </div>
          </Space>
        </Col>
        <Col className={'flex justify-end'}>
          <Space size={'large'}>
            <div style={{ width: 200 }}>
              <div className={'filter-label'}>Status</div>
              <Segmented
                size={'large'}
                block
                className={status === 'active' ? 'npi-blue' : 'default'}
                value={status}
                onChange={(value: any) => handleActiveExpiredClick(value)}
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'expired' }
                ]}
              />
            </div>
          </Space>
        </Col>
        <Col className={'flex justify-end'}>
          <Space size={'large'}>
            <div style={{ width: 310 }}>
              <div className={'filter-label'}>
                Renewal Zone
                <Popover
                  content={<div style={{ width: 550 }}>{RENEWAL_ZONE_CONTENT}</div>}
                  title={<h2>What is a Renewal Zone?</h2>}
                  trigger="hover"
                  placement="topRight"
                >
                  <QuestionCircleOutlined className="ml-2 cursor-pointer" style={{ fontSize: 14 }} />
                </Popover>
              </div>
              <Segmented
                size={'large'}
                className={segmentedClass}
                block
                onChange={(value: any) => handleZoneClick(value)}
                value={segmentedClass}
                options={[
                  { label: 'All', value: 'default' },
                  { label: 'Orange', value: 'orange', icon: <WarningOutlined /> },
                  { label: 'Red', value: 'red', icon: <ExclamationCircleOutlined /> }
                ]}
              />
            </div>
          </Space>
        </Col>
      </Row>
    </ConfigProvider>
  );
};
